import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutGrid, Boxes, FlaskConical, Newspaper, Briefcase, Inbox, User, LogOut, Plus, Pencil, Trash2, X, ExternalLink,
  Activity, RefreshCw, Save,
} from "lucide-react";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useContent } from "@/context/ContentContext";
import StatusBadge from "@/components/StatusBadge";
import Uploader from "@/components/Uploader";
import RichTextEditor from "@/components/RichTextEditor";
import SEO from "@/components/SEO";

const STATUSES = ["LIVE", "BETA", "PRIVATE BETA", "IN DEVELOPMENT", "PROTOTYPE", "CONCEPT", "IMJOVE LABS", "IMJOVE TECHNOLOGY", "COMING SOON"];
const STAGES = ["", "CONCEPT", "RESEARCH", "PROTOTYPE", "DEVELOPMENT", "PRIVATE BETA", "COMING SOON"];

const PROJECT_FIELDS = [
  { k: "name", label: "Name", req: true },
  { k: "slug", label: "Slug (auto if blank)" },
  { k: "tagline", label: "Tagline" },
  { k: "industry", label: "Industry" },
  { k: "status", label: "Status", type: "select", opts: STATUSES },
  { k: "website_url", label: "Website URL" },
  { k: "cta_label", label: "CTA Label" },
  { k: "accent", label: "Accent color (hex)", type: "color" },
  { k: "logo", label: "Logo", type: "image" },
  { k: "description", label: "Short description", type: "textarea" },
  { k: "long_description", label: "Long description", type: "textarea" },
  { k: "features", label: "Features (one per line)", type: "array" },
  { k: "technology", label: "Technology (one per line)", type: "array" },
  { k: "images", label: "Screenshots / images", type: "gallery" },
  { k: "videos", label: "Videos", type: "videos" },
  { k: "category", label: "Category (labs)" },
  { k: "development_stage", label: "Development stage (labs)", type: "select", opts: STAGES },
  { k: "launch_date", label: "Launch date" },
  { k: "display_order", label: "Display order", type: "number" },
  { k: "featured", label: "Featured on homepage", type: "bool" },
  { k: "imjove_labs", label: "ImJove Labs project", type: "bool" },
  { k: "seo_title", label: "SEO title" },
  { k: "seo_description", label: "SEO description", type: "textarea" },
];

const ARTICLE_FIELDS = [
  { k: "title", label: "Title", req: true },
  { k: "slug", label: "Slug (auto if blank)" },
  { k: "category", label: "Category" },
  { k: "author", label: "Author" },
  { k: "cover_image", label: "Cover image", type: "image" },
  { k: "excerpt", label: "Excerpt", type: "textarea" },
  { k: "body", label: "Body", type: "richtext" },
  { k: "published", label: "Published", type: "bool" },
  { k: "seo_title", label: "SEO title" },
  { k: "seo_description", label: "SEO description", type: "textarea" },
];

const CAREER_FIELDS = [
  { k: "title", label: "Title", req: true },
  { k: "department", label: "Department" },
  { k: "location", label: "Location" },
  { k: "type", label: "Type" },
  { k: "description", label: "Description", type: "textarea" },
  { k: "apply_url", label: "Apply URL (optional)" },
  { k: "published", label: "Published", type: "bool" },
];

const inputCls = "w-full rounded-lg border border-white/10 bg-ink-900 px-3 py-2.5 font-body text-sm text-white placeholder:text-white/30 outline-none focus:border-gold";

function Field({ f, value, onChange }) {
  if (f.type === "bool")
    return (
      <label className="flex items-center gap-3 py-2">
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 accent-gold" data-testid={`field-${f.k}`} />
        <span className="font-body text-sm text-white/70">{f.label}</span>
      </label>
    );
  if (f.type === "image")
    return (
      <div>
        <label className="font-mono2 text-[10px] uppercase tracking-widest text-white/40">{f.label}</label>
        <div className="mt-1.5 flex items-center gap-3">
          {value ? (
            <img src={value} alt="" className="h-14 w-14 rounded-lg border border-white/10 object-cover" />
          ) : (
            <div className="flex h-14 w-14 items-center justify-center rounded-lg border border-dashed border-white/15 text-[9px] text-white/30">none</div>
          )}
          <Uploader accept="image/*" label="Upload" testid={`upload-${f.k}`} onUploaded={onChange} />
          {value && <button type="button" onClick={() => onChange("")} className="font-mono2 text-[10px] uppercase tracking-widest text-white/40 hover:text-red-400">Remove</button>}
        </div>
        <input data-testid={`field-${f.k}`} className={`${inputCls} mt-2`} placeholder="…or paste a URL" value={value || ""} onChange={(e) => onChange(e.target.value)} />
      </div>
    );
  if (f.type === "gallery" || f.type === "videos") {
    const arr = Array.isArray(value) ? value : [];
    const isVideo = f.type === "videos";
    return (
      <div>
        <label className="font-mono2 text-[10px] uppercase tracking-widest text-white/40">{f.label}</label>
        <div className="mt-2 flex flex-wrap gap-3">
          {arr.map((u, i) => (
            <div key={i} className="relative">
              {isVideo ? (
                <video src={u} className="h-16 w-24 rounded-lg border border-white/10 object-cover" />
              ) : (
                <img src={u} alt="" className="h-16 w-24 rounded-lg border border-white/10 object-cover" />
              )}
              <button type="button" onClick={() => onChange(arr.filter((_, j) => j !== i))} className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] text-white">×</button>
            </div>
          ))}
        </div>
        <div className="mt-2">
          <Uploader accept={isVideo ? "video/*" : "image/*"} label={isVideo ? "Upload video" : "Upload image"} testid={`upload-${f.k}`} onUploaded={(url) => onChange([...arr, url])} />
        </div>
      </div>
    );
  }
  if (f.type === "richtext")
    return (
      <div>
        <label className="font-mono2 text-[10px] uppercase tracking-widest text-white/40">{f.label}</label>
        <div className="mt-1.5"><RichTextEditor value={value} onChange={onChange} /></div>
      </div>
    );
  return (
    <div>
      <label className="font-mono2 text-[10px] uppercase tracking-widest text-white/40">{f.label}</label>
      {f.type === "textarea" ? (
        <textarea data-testid={`field-${f.k}`} className={`${inputCls} mt-1.5 ${f.big ? "min-h-[180px]" : "min-h-[80px]"}`} value={value || ""} onChange={(e) => onChange(e.target.value)} />
      ) : f.type === "array" ? (
        <textarea data-testid={`field-${f.k}`} className={`${inputCls} mt-1.5 min-h-[90px]`} value={Array.isArray(value) ? value.join("\n") : value || ""} onChange={(e) => onChange(e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))} />
      ) : f.type === "select" ? (
        <select data-testid={`field-${f.k}`} className={`${inputCls} mt-1.5`} value={value || ""} onChange={(e) => onChange(e.target.value)}>
          {f.opts.map((o) => <option key={o} value={o} className="bg-ink-900">{o || "—"}</option>)}
        </select>
      ) : f.type === "number" ? (
        <input type="number" data-testid={`field-${f.k}`} className={`${inputCls} mt-1.5`} value={value ?? 0} onChange={(e) => onChange(Number(e.target.value))} />
      ) : (
        <input data-testid={`field-${f.k}`} className={`${inputCls} mt-1.5`} value={value || ""} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
}

function Editor({ fields, item, onClose, onSave }) {
  const [form, setForm] = useState(item || {});
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <div className="fixed inset-0 z-[60] flex justify-end bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="glass h-full w-full max-w-xl overflow-y-auto p-8" onClick={(e) => e.stopPropagation()} data-testid="editor-panel">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-2xl font-semibold text-white">{item?.id ? "Edit" : "Create"}</h3>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X size={20} /></button>
        </div>
        <div className="mt-6 space-y-4">
          {fields.map((f) => <Field key={f.k} f={f} value={form[f.k]} onChange={set(f.k)} />)}
        </div>
        <div className="mt-8 flex gap-3">
          <button
            data-testid="editor-save"
            className="btn-gold rounded-full px-6 py-3 font-mono2 text-[11px] font-semibold uppercase tracking-widest"
            onClick={() => {
              const req = fields.find((f) => f.req && !form[f.k]);
              if (req) return toast.error(`${req.label} is required`);
              onSave(form);
            }}
          >
            Save
          </button>
          <button onClick={onClose} className="btn-ghost rounded-full px-6 py-3 font-mono2 text-[11px] uppercase tracking-widest">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function EntityManager({ endpoint, fields, title, filter, renderMeta }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const filterRef = useRef(filter);
  filterRef.current = filter;

  const load = useCallback(() => {
    client.get(endpoint, { params: { all: true } }).then(({ data }) => setItems(filterRef.current ? data.filter(filterRef.current) : data)).catch(() => {});
  }, [endpoint]);

  useEffect(() => { load(); }, [load]);

  const save = async (form) => {
    try {
      if (form.id) await client.put(`${endpoint}/${form.id}`, form);
      else await client.post(endpoint, form);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error("Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await client.delete(`${endpoint}/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <h2 className="font-display text-2xl font-semibold text-white">{title}</h2>
        <button data-testid={`add-${title.toLowerCase()}`} onClick={() => setEditing({})} className="btn-gold inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-mono2 text-[11px] font-semibold uppercase tracking-widest">
          <Plus size={14} /> New
        </button>
      </div>
      <div className="mt-6 space-y-3">
        {items.map((it) => (
          <div key={it.id} data-testid={`row-${it.slug || it.id}`} className="flex items-center justify-between rounded-xl border border-white/10 bg-ink-800 p-4">
            <div className="min-w-0">
              <p className="truncate font-display text-white">{it.name || it.title}</p>
              <div className="mt-1 flex items-center gap-2">{renderMeta && renderMeta(it)}</div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <button onClick={() => setEditing(it)} className="rounded-lg border border-white/10 p-2 text-white/60 hover:text-gold-light" data-testid={`edit-${it.slug || it.id}`}><Pencil size={14} /></button>
              <button onClick={() => remove(it.id)} className="rounded-lg border border-white/10 p-2 text-white/60 hover:text-red-400" data-testid={`delete-${it.slug || it.id}`}><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="font-body text-sm text-white/40">Nothing here yet.</p>}
      </div>
      {editing && <Editor fields={fields} item={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function InquiriesPanel() {
  const [items, setItems] = useState([]);
  const load = () => client.get("/inquiries").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => { await client.patch(`/inquiries/${id}`, { status }); load(); };
  const remove = async (id) => { if (!window.confirm("Delete?")) return; await client.delete(`/inquiries/${id}`); load(); };
  return (
    <div>
      <h2 className="font-display text-2xl font-semibold text-white">Inquiries</h2>
      <div className="mt-6 space-y-3">
        {items.map((q) => (
          <div key={q.id} className="rounded-xl border border-white/10 bg-ink-800 p-5" data-testid={`inquiry-${q.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="font-display text-white">{q.name} <span className="font-body text-sm text-white/40">· {q.email}</span></p>
                <p className="font-mono2 text-[10px] uppercase tracking-widest text-gold-light">{q.inquiry_type}{q.company ? ` · ${q.company}` : ""}</p>
              </div>
              <div className="flex items-center gap-2">
                <select value={q.status} onChange={(e) => setStatus(q.id, e.target.value)} className={`${inputCls} w-auto py-1.5`}>
                  {["new", "in progress", "closed"].map((s) => <option key={s} value={s} className="bg-ink-900">{s}</option>)}
                </select>
                <button onClick={() => remove(q.id)} className="rounded-lg border border-white/10 p-2 text-white/60 hover:text-red-400"><Trash2 size={14} /></button>
              </div>
            </div>
            <p className="mt-3 font-body text-sm text-white/60">{q.message}</p>
          </div>
        ))}
        {items.length === 0 && <p className="font-body text-sm text-white/40">No inquiries yet.</p>}
      </div>
    </div>
  );
}

function humanize(k) {
  return String(k).replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
function fmt(v) {
  if (typeof v === "number") return v.toLocaleString();
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (v === null) return "—";
  return String(v);
}
function collectMetrics(obj, prefix = "", depth = 0, out = []) {
  if (!obj || typeof obj !== "object") return out;
  Object.entries(obj).forEach(([k, v]) => {
    const label = prefix ? `${prefix} · ${humanize(k)}` : humanize(k);
    if (v === null || ["string", "number", "boolean"].includes(typeof v)) {
      out.push({ label, value: fmt(v) });
    } else if (Array.isArray(v)) {
      out.push({ label, value: `${v.length} item${v.length === 1 ? "" : "s"}` });
    } else if (typeof v === "object" && depth < 1) {
      collectMetrics(v, label, depth + 1, out);
    }
  });
  return out;
}

function PlatformCard({ cfg, onSaved }) {
  const [form, setForm] = useState(cfg);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await client.put(`/admin/integrations/${cfg.platform}`, {
        base_url: form.base_url, stats_path: form.stats_path, auth_type: form.auth_type, name: form.name,
      });
      toast.success("Settings saved");
      onSaved && onSaved(data);
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const fetchStats = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await client.get(`/admin/performance/${cfg.platform}`);
      setResult(data);
      if (data.ok) { setForm((f) => ({ ...f, stats_path: data.endpoint, auth_type: data.auth_type })); toast.success(`${cfg.name} connected`); }
    } catch (e) { setResult({ ok: false, error: "Request failed" }); } finally { setLoading(false); }
  };

  const metrics = result?.ok ? collectMetrics(result.data) : [];

  return (
    <div className="rounded-2xl border border-white/10 bg-ink-800 p-6" data-testid={`platform-card-${cfg.platform}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gold/10 text-gold-light"><Activity size={18} /></div>
          <div>
            <h3 className="font-display text-xl font-semibold text-white">{cfg.name}</h3>
            <p className="font-mono2 text-[10px] uppercase tracking-widest text-white/40">
              API key {cfg.key_configured ? <span className="text-emerald-400">configured</span> : <span className="text-red-400">missing</span>}
            </p>
          </div>
        </div>
        <button onClick={fetchStats} disabled={loading} data-testid={`fetch-${cfg.platform}`} className="btn-gold inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-mono2 text-[11px] font-semibold uppercase tracking-widest disabled:opacity-60">
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> {loading ? "Fetching…" : "Fetch metrics"}
        </button>
      </div>

      {result && result.ok && (
        <div className="mt-6">
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {metrics.length === 0 && <p className="font-body text-sm text-white/50">Connected, but no simple metrics found — see raw response below.</p>}
            {metrics.map((m, i) => (
              <div key={i} className="rounded-xl border border-white/10 bg-ink-900 p-4">
                <p className="font-mono2 text-[10px] uppercase tracking-widest text-white/40">{m.label}</p>
                <p className="mt-2 font-display text-2xl font-bold text-gold-gradient">{m.value}</p>
              </div>
            ))}
          </div>
          <details className="mt-4">
            <summary className="cursor-pointer font-mono2 text-[10px] uppercase tracking-widest text-white/40 hover:text-white">Raw response · {result.url}</summary>
            <pre className="mt-2 max-h-72 overflow-auto rounded-lg border border-white/10 bg-ink-900 p-4 font-mono2 text-[11px] text-white/60">{JSON.stringify(result.data, null, 2)}</pre>
          </details>
        </div>
      )}

      {result && !result.ok && (
        <div className="mt-5 rounded-xl border border-red-500/20 bg-red-500/5 p-4">
          <p className="font-body text-sm text-red-300">{result.error}</p>
          {result.tried && (
            <details className="mt-2">
              <summary className="cursor-pointer font-mono2 text-[10px] uppercase tracking-widest text-white/40">What was tried</summary>
              <ul className="mt-2 space-y-1">
                {result.tried.map((t, i) => (
                  <li key={i} className="font-mono2 text-[10px] text-white/45">{t.url} <span className="text-white/30">({t.auth})</span> → {t.result}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      <div className="mt-6 border-t border-white/10 pt-5">
        <p className="font-mono2 text-[10px] uppercase tracking-widest text-white/40">Connection settings</p>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <div>
            <label className="font-mono2 text-[10px] uppercase tracking-widest text-white/40">Base URL</label>
            <input data-testid={`base-url-${cfg.platform}`} className={`${inputCls} mt-1.5`} placeholder="https://api.example.com" value={form.base_url || ""} onChange={set("base_url")} />
          </div>
          <div>
            <label className="font-mono2 text-[10px] uppercase tracking-widest text-white/40">Stats path</label>
            <input data-testid={`stats-path-${cfg.platform}`} className={`${inputCls} mt-1.5`} placeholder="/v1/stats" value={form.stats_path || ""} onChange={set("stats_path")} />
          </div>
          <div>
            <label className="font-mono2 text-[10px] uppercase tracking-widest text-white/40">Auth style</label>
            <select className={`${inputCls} mt-1.5`} value={form.auth_type || "bearer"} onChange={set("auth_type")}>
              <option value="bearer" className="bg-ink-900">Bearer token</option>
              <option value="x-api-key" className="bg-ink-900">X-API-Key header</option>
            </select>
          </div>
          <div className="flex items-end">
            <button onClick={save} disabled={saving} className="btn-ghost inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-mono2 text-[11px] uppercase tracking-widest">
              <Save size={13} /> {saving ? "Saving…" : "Save settings"}
            </button>
          </div>
        </div>
        <p className="mt-3 font-body text-xs text-white/35">The API key is stored securely on the server and never shown here. Update the base URL / path above to match your platform's API, then Fetch metrics.</p>
      </div>
    </div>
  );
}

function PerformancePanel() {
  const [cfgs, setCfgs] = useState([]);
  const load = () => client.get("/admin/integrations").then(({ data }) => setCfgs(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  return (
    <div>
      <h2 className="font-display text-2xl font-semibold text-white">Platform Performance</h2>
      <p className="mt-2 font-body text-white/50">Live metrics from each connected ImJove platform. Keys are stored securely server-side.</p>
      <div className="mt-6 space-y-6">
        {cfgs.map((c) => (
          <PlatformCard key={c.platform} cfg={c} onSaved={(d) => setCfgs((prev) => prev.map((p) => (p.platform === d.platform ? { ...p, ...d } : p)))} />
        ))}
        {cfgs.length === 0 && <p className="font-body text-sm text-white/40">No platforms configured.</p>}
      </div>
    </div>
  );
}

function ContentPanel() {
  const { content, refresh } = useContent();
  const [form, setForm] = useState(content);
  useEffect(() => setForm(content), [content]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setSocial = (k) => (e) => setForm((f) => ({ ...f, social_links: { ...(f.social_links || {}), [k]: e.target.value } }));
  const save = async () => { try { await client.put("/content", form); toast.success("Content saved"); refresh(); } catch { toast.error("Save failed"); } };
  const social = form.social_links || {};
  const TEXT = [
    ["logo_url", "Logo URL"], ["founder_portrait", "Founder portrait URL"],
    ["hero_headline_1", "Hero headline line 1"], ["hero_headline_2", "Hero headline line 2"],
    ["hero_sub", "Hero subheading"], ["founder_name", "Founder name"], ["founder_role", "Founder role"],
    ["founder_subheadline", "Founder subheadline"], ["mission_headline", "Mission headline"], ["founder_quote", "Founder quote"],
  ];
  const AREA = [["hero_body", "Hero body"], ["founder_bio", "Founder bio"], ["mission_body", "Mission body"]];
  return (
    <div className="max-w-2xl">
      <h2 className="font-display text-2xl font-semibold text-white">Founder & Company Content</h2>
      <div className="mt-6 space-y-4">
        {TEXT.map(([k, label]) => (
          <div key={k}>
            <label className="font-mono2 text-[10px] uppercase tracking-widest text-white/40">{label}</label>
            <input data-testid={`content-${k}`} className={`${inputCls} mt-1.5`} value={form[k] || ""} onChange={set(k)} />
          </div>
        ))}
        {AREA.map(([k, label]) => (
          <div key={k}>
            <label className="font-mono2 text-[10px] uppercase tracking-widest text-white/40">{label}</label>
            <textarea data-testid={`content-${k}`} className={`${inputCls} mt-1.5 min-h-[140px]`} value={form[k] || ""} onChange={set(k)} />
          </div>
        ))}
        <div className="grid grid-cols-2 gap-4">
          {["instagram", "facebook"].map((s) => (
            <div key={s}>
              <label className="font-mono2 text-[10px] uppercase tracking-widest text-white/40">{s}</label>
              <input data-testid={`content-social-${s}`} className={`${inputCls} mt-1.5`} value={social[s] || ""} onChange={setSocial(s)} />
            </div>
          ))}
        </div>
        <button onClick={save} data-testid="content-save" className="btn-gold rounded-full px-6 py-3 font-mono2 text-[11px] font-semibold uppercase tracking-widest">Save Content</button>
      </div>
    </div>
  );
}

const TABS = [
  { k: "overview", label: "Overview", icon: LayoutGrid },
  { k: "projects", label: "Companies", icon: Boxes },
  { k: "labs", label: "ImJove Labs", icon: FlaskConical },
  { k: "newsroom", label: "Newsroom", icon: Newspaper },
  { k: "careers", label: "Careers", icon: Briefcase },
  { k: "inquiries", label: "Inquiries", icon: Inbox },
  { k: "performance", label: "Performance", icon: Activity },
  { k: "content", label: "Founder & Content", icon: User },
];

export default function AdminDashboard() {
  const { logout, user } = useAuth();
  const { content } = useContent();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState({ projects: 0, articles: 0, careers: 0, inquiries: 0 });

  useEffect(() => {
    Promise.all([
      client.get("/projects"),
      client.get("/articles", { params: { all: true } }),
      client.get("/careers", { params: { all: true } }),
      client.get("/inquiries"),
    ]).then(([p, a, c, i]) => setStats({ projects: p.data.length, articles: a.data.length, careers: c.data.length, inquiries: i.data.length })).catch(() => {});
  }, [tab]);

  const doLogout = () => { logout(); navigate("/admin"); };
  const meta = (it) => (<><StatusBadge status={it.status} /><span className="font-mono2 text-[10px] text-white/40">{it.industry}</span></>);
  const artMeta = (it) => (<span className={`font-mono2 text-[10px] uppercase tracking-widest ${it.published ? "text-emerald-400" : "text-white/40"}`}>{it.published ? "Published" : "Draft"} · {it.category}</span>);
  const carMeta = (it) => (<span className="font-mono2 text-[10px] uppercase tracking-widest text-white/40">{it.department} · {it.location}</span>);

  return (
    <div className="flex min-h-screen bg-ink-900">
      <SEO title="Dashboard — ImJove Tech" description="ImJove Tech control center." />
      <aside className="fixed inset-y-0 left-0 hidden w-64 flex-col border-r border-white/10 bg-ink-800 p-6 lg:flex">
        <div className="flex items-center gap-3">
          <img src={content.logo_url} alt="ImJove" className="h-9 w-9 object-contain" />
          <span className="font-display text-sm font-semibold uppercase tracking-brand text-white">Control Center</span>
        </div>
        <nav className="mt-10 flex-1 space-y-1">
          {TABS.map((t) => (
            <button key={t.k} data-testid={`tab-${t.k}`} onClick={() => setTab(t.k)} className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 font-body text-sm transition-colors ${tab === t.k ? "bg-gold/10 text-gold-light" : "text-white/55 hover:bg-white/5 hover:text-white"}`}>
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </nav>
        <a href="/" target="_blank" rel="noreferrer" className="mb-2 flex items-center gap-2 px-3 font-mono2 text-[11px] uppercase tracking-widest text-white/40 hover:text-white"><ExternalLink size={13} /> View site</a>
        <button onClick={doLogout} data-testid="admin-logout" className="flex items-center gap-2 px-3 font-mono2 text-[11px] uppercase tracking-widest text-white/40 hover:text-red-400"><LogOut size={13} /> Logout</button>
      </aside>

      <div className="flex-1 lg:ml-64">
        <header className="glass sticky top-0 z-40 flex items-center justify-between px-6 py-4">
          <div className="flex gap-2 overflow-x-auto lg:hidden">
            {TABS.map((t) => (
              <button key={t.k} onClick={() => setTab(t.k)} className={`whitespace-nowrap rounded-full px-3 py-1.5 font-mono2 text-[10px] uppercase tracking-widest ${tab === t.k ? "bg-gold/15 text-gold-light" : "text-white/50"}`}>{t.label}</button>
            ))}
          </div>
          <p className="hidden font-mono2 text-[11px] uppercase tracking-widest text-white/40 lg:block">Signed in as {user?.name}</p>
          <button onClick={doLogout} className="font-mono2 text-[11px] uppercase tracking-widest text-white/40 hover:text-red-400 lg:hidden">Logout</button>
        </header>

        <main className="p-6 md:p-10">
          {tab === "overview" && (
            <div>
              <h1 className="font-display text-3xl font-bold tracking-tighter text-white">Welcome, {user?.name}.</h1>
              <p className="mt-2 font-body text-white/50">Manage the entire ImJove Tech ecosystem from here.</p>
              <div className="mt-8 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                {[["Companies", stats.projects], ["Articles", stats.articles], ["Careers", stats.careers], ["Inquiries", stats.inquiries]].map(([l, v]) => (
                  <div key={l} className="rounded-2xl border border-white/10 bg-ink-800 p-6">
                    <p className="font-mono2 text-[10px] uppercase tracking-widest text-white/40">{l}</p>
                    <p className="mt-3 font-display text-4xl font-bold text-gold-gradient">{v}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          {tab === "projects" && <EntityManager endpoint="/projects" fields={PROJECT_FIELDS} title="Companies" filter={(p) => !p.imjove_labs} renderMeta={meta} />}
          {tab === "labs" && <EntityManager endpoint="/projects" fields={PROJECT_FIELDS} title="ImJove Labs" filter={(p) => p.imjove_labs} renderMeta={meta} />}
          {tab === "newsroom" && <EntityManager endpoint="/articles" fields={ARTICLE_FIELDS} title="Newsroom" renderMeta={artMeta} />}
          {tab === "careers" && <EntityManager endpoint="/careers" fields={CAREER_FIELDS} title="Careers" renderMeta={carMeta} />}
          {tab === "inquiries" && <InquiriesPanel />}
          {tab === "performance" && <PerformancePanel />}
          {tab === "content" && <ContentPanel />}
        </main>
      </div>
    </div>
  );
}
